// Effective permissions on a Business Space — identity plan §10.1 and §10.3.
import type { BusinessRole, Capability, Permission } from "../identity/types";

const OWNER_ONLY: readonly Permission[] = [
  "space.ownership.transfer",
  "space.delete",
  "space.convert",
  "commerce.payouts.manage",
];

const COMMERCE: readonly Permission[] = [
  "commerce.products.write",
  "commerce.products.publish",
  "commerce.orders.read",
  "commerce.customers.read",
  "commerce.analytics.read",
  "commerce.payouts.manage",
];

const ALL: readonly Permission[] = [
  "space.profile.edit",
  "space.modules.manage",
  "space.members.invite",
  "space.members.manage",
  "space.posts.moderate",
  "space.act_as",
  ...OWNER_ONLY.filter((permission) => !COMMERCE.includes(permission)),
  ...COMMERCE,
];

/** Business role presets (§10.1). Staff get assigned modules only, which V1 doesn't model, so nothing here. */
const ROLE_BUNDLES: Record<BusinessRole, readonly Permission[]> = {
  admin: ALL.filter((permission) => !OWNER_ONLY.includes(permission)),
  manager: [
    "space.modules.manage",
    "space.members.invite",
    "space.members.manage",
    ...COMMERCE.filter((permission) => !OWNER_ONLY.includes(permission)),
  ],
  staff: [],
};

export interface SpaceAuthority {
  isOwner: boolean;
  /** The Person's membership, or null when they aren't a member. */
  membership: { role: BusinessRole; status: "active" | "banned" | "removed" } | null;
  capabilities: readonly Capability[];
}

/** Owner → everything; active member → the role bundle; commerce needs the capability, even for the owner. */
export function effectivePermissions({ isOwner, membership, capabilities }: SpaceAuthority): Set<Permission> {
  const permissions = new Set<Permission>();
  if (isOwner) ALL.forEach((permission) => permissions.add(permission));
  else if (membership?.status === "active") ROLE_BUNDLES[membership.role].forEach((permission) => permissions.add(permission));
  if (!capabilities.includes("commerce")) COMMERCE.forEach((permission) => permissions.delete(permission));
  return permissions;
}
